import { useTranslation } from 'react-i18next'
import { ActivityIndicator, Image, TouchableOpacity, View } from 'react-native'
import { Button, Text, useTheme } from 'react-native-paper'
import Toast from 'react-native-root-toast'
import { useEffect, useState } from 'react'
import {
  NavigationProp,
  useNavigation,
  useRoute,
} from '@react-navigation/native'
import { AlipayPNG } from '../../../utils/meldd-icons'
import { Env } from '../../../env'
import { AppNavigatorParams } from '../../../navigation/types'
import { Column, Icon } from '../../../@ui'
import { PaymentConfirmationModal } from './payment-confirmation.modal'
import { createStyle } from '../../../contexts/global-styles.provider'
import { PaypalChannel } from './paypal-channel'
import { WechatChannel } from './wechat-channel'
import { useCurrency } from './payment.utils'
import { MelddModal } from '../../../@ui/containers/modals'
import {
  isPaymentChannelEnabled,
  PaymentType,
} from './payment-visibility.registry'
import { CouponPreview, PaymentCouponSection } from './payment-coupon.section'

type PaymentChannelModalProps = {
  onDismiss: () => void
  allowCoupon?: boolean
  billing?: {
    billingId: string
    amount: number
    basePrice: number
  }
}

function couponCodeFromNodeSettingsRoute(route: {
  name: string
  params?: unknown
}): string {
  if (route.name !== 'NodeSettings') return ''
  const params = route.params as { couponCode?: string } | undefined
  return (params?.couponCode ?? '').trim()
}

export const PaymentIcon = ({
  source,
  loading,
  onPress,
  label,
  emphasized,
}: {
  source: React.ReactNode
  loading: boolean
  onPress: () => void
  label: string
  /** Visually marks this option (e.g. coupon prefilled from link) without hiding others */
  emphasized?: boolean
}) => {
  const theme = useTheme()
  if (loading) {
    return <ActivityIndicator size="small" color={theme.colors.primary} />
  }
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityState={emphasized ? { selected: true } : undefined}
    >
      <Column
        alignItems="center"
        gap={4}
        style={
          emphasized
            ? {
                paddingVertical: 8,
                paddingHorizontal: 10,
                borderRadius: 14,
                borderWidth: 2,
                borderColor: theme.colors.primary,
                backgroundColor: theme.colors.primaryContainer,
              }
            : undefined
        }
      >
        <Column
          style={{
            width: 48,
            height: 48,
            backgroundColor: emphasized ? theme.colors.surface : 'white',
            borderRadius: 24,
          }}
          justifyContent="center"
          alignItems="center"
        >
          {source}
        </Column>
        <Text
          variant="bodyMedium"
          style={
            emphasized
              ? { color: theme.colors.primary, fontWeight: '600' }
              : undefined
          }
        >
          {label}
        </Text>
      </Column>
    </TouchableOpacity>
  )
}

const DevChannel = ({
  billingId,
  onProcess,
}: {
  billingId: string
  onProcess: () => void
}) => {
  const { t } = useTranslation('tiers')
  const [loading, setLoading] = useState(false)

  if (!isPaymentChannelEnabled(PaymentType.DEV)) {
    return null
  }

  const handleDevPayment = async () => {
    if (!billingId) {
      Toast.show('No billing ID available', {
        position: Toast.positions.CENTER,
      })
      return
    }

    setLoading(true)
    try {
      const response = await fetch(
        `${Env.API_URL.replace(
          '/graphql',
          ''
        )}/payment-callback/dev/${billingId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            success: true,
            transactionId: `dev-${Date.now()}`, // Generate a unique transaction ID
          }),
        }
      )
      if (!response.ok) {
        Toast.show(`Dev payment failed`, {
          position: Toast.positions.CENTER,
        })
        return
      }
      onProcess()
    } catch (error) {
      console.error('Dev payment error:', error)
      Toast.show('Error processing dev payment', {
        position: Toast.positions.CENTER,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <PaymentIcon
      label={`${t('payment.dev')}`}
      source={<Icon source="link" size={24} />}
      loading={loading}
      onPress={handleDevPayment}
    />
  )
}

const AlipayChannel = () => {
  const { t } = useTranslation('tiers')

  if (!isPaymentChannelEnabled(PaymentType.ALIPAY)) {
    return null
  }
  return (
    <PaymentIcon
      label={t('payment.alipay')}
      source={<Image source={AlipayPNG} style={[{ width: 32, height: 32 }]} />}
      loading={false}
      onPress={() => Toast.show('Not implemented yet')}
    />
  )
}

function endOfCurrentMonthLabel(): string {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).toLocaleDateString()
}

function roundCurrency(amount: number): number {
  return Math.round(amount * 100) / 100
}

function renewalDateFromAppliedMonths(months: number): string {
  const renewalDate = new Date()
  renewalDate.setMonth(renewalDate.getMonth() + months)
  return renewalDate.toLocaleDateString()
}

/**
 * Display payment channel selection modal
 */
export const PaymentChannelModal = ({
  onDismiss,
  allowCoupon = true,
  billing,
}: PaymentChannelModalProps) => {
  const { t } = useTranslation('node-tier')
  const styles = useStyles()
  const route = useRoute()
  const prefilledCoupon = couponCodeFromNodeSettingsRoute(route)
  const [appliedCoupon, setAppliedCoupon] = useState<CouponPreview | null>(null)
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false)
  const navigation = useNavigation<NavigationProp<AppNavigatorParams>>()

  /**
   * PRICE
   */
  const price = useCurrency(billing?.basePrice ?? 0)
  const currentAmount = (() => {
    if (!billing) {
      return 0
    }
    if (!appliedCoupon) {
      return billing.amount
    }
    const discount = appliedCoupon.discountPercentage ?? 0
    return roundCurrency(billing.basePrice * ((100 - discount) / 100))
  })()

  const tilNextMonth = useCurrency(currentAmount)
  const renewalLabel = (() => {
    if (!appliedCoupon) {
      return t('subscription-renewal-on-date', {
        date: endOfCurrentMonthLabel(),
      })
    }
    const appliedMonths = appliedCoupon.appliedMonths
    if (
      appliedMonths === 0 ||
      appliedMonths === null ||
      appliedMonths === undefined
    ) {
      return t('subscription-no-renovation-coupon-forever')
    }
    return t('subscription-renewal-on-date', {
      date: renewalDateFromAppliedMonths(appliedMonths),
    })
  })()

  useEffect(() => {
    setAppliedCoupon(null)
  }, [billing?.billingId, prefilledCoupon])

  if (!billing) {
    return null
  }

  const handleCouponChange = (coupon: CouponPreview | null) => {
    setAppliedCoupon(coupon)
  }

  return (
    <>
      <MelddModal
        visible={!!billing}
        onDismiss={onDismiss}
        title={t('select-payment-method')}
        width={styles.modalWrapper.width}
        scrollable
        scrollContentContainerStyle={styles.modalScrollContent}
        body={
          <View style={styles.modalContent}>
            <Column gap={4} alignItems="center">
              <Text style={{ textAlign: 'center' }} variant="titleMedium">
                {t('pay-price')}
              </Text>
              {appliedCoupon ? (
                <View style={styles.priceRow}>
                  <Text style={styles.originalPriceText}>{price}</Text>
                  <Text style={styles.discountedPriceText}>{tilNextMonth}</Text>
                </View>
              ) : (
                <Text
                  style={{
                    fontSize: 24,
                    fontWeight: 'bold',
                    textAlign: 'center',
                  }}
                >
                  {price}
                </Text>
              )}
              <Text style={{ fontSize: 16, textAlign: 'center' }}>
                {t('pay-today')} {tilNextMonth}
              </Text>
              <Text variant="bodyMedium" style={{ textAlign: 'center' }}>
                {renewalLabel}
              </Text>

              {allowCoupon ? (
                <View style={styles.couponSection}>
                  <PaymentCouponSection
                    billingId={billing.billingId}
                    prefilledCoupon={prefilledCoupon}
                    onCouponChange={handleCouponChange}
                    onFreeCouponProcessed={() =>
                      setShowPaymentConfirmation(true)
                    }
                  />
                </View>
              ) : null}

              <View style={styles.channelGrid}>
                {appliedCoupon?.isFree ? null : (
                  <>
                    <DevChannel
                      billingId={billing?.billingId}
                      onProcess={() => setShowPaymentConfirmation(true)}
                    />
                    <PaypalChannel
                      billingId={billing?.billingId}
                      amount={currentAmount}
                      onProcess={() => setShowPaymentConfirmation(true)}
                    />
                    <WechatChannel
                      billingId={billing?.billingId}
                      description="Node tier payment"
                      onProcess={() => setShowPaymentConfirmation(true)}
                    />
                    <AlipayChannel />
                  </>
                )}
              </View>
            </Column>
          </View>
        }
        actions={<Button onPress={onDismiss}>{t('cancel')}</Button>}
      />

      <PaymentConfirmationModal
        visible={showPaymentConfirmation}
        billingId={billing?.billingId || ''}
        onSuccess={() => {
          setShowPaymentConfirmation(false)
          onDismiss()

          navigation.navigate('MyNodes')
          Toast.show('Payment completed successfully!', {
            position: Toast.positions.CENTER,
          })
        }}
        onError={() => {
          Toast.show('Payment failed or timed out', {
            position: Toast.positions.CENTER,
          })
        }}
        onClose={() => setShowPaymentConfirmation(false)}
      />
    </>
  )
}

const useStyles = createStyle(({ isMobile, theme }) => ({
  modalWrapper: {
    width: isMobile ? '80%' : 400,
    marginHorizontal: 'auto',
  },
  modalScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    width: '100%',
    paddingVertical: 4,
  },
  modalContent: {
    width: '100%',
    maxWidth: '100%',
  },
  channelGrid: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    alignItems: 'center',
    gap: 16,
  },
  channelItem: {
    alignItems: 'center',
    minWidth: 80,
  },
  channelButton: {
    margin: 0,
  },
  channelLabel: {
    marginTop: 8,
    textAlign: 'center',
  },
  priceRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  originalPriceText: {
    fontSize: 18,
    textDecorationLine: 'line-through',
    opacity: 0.7,
  },
  discountedPriceText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: theme.colors.primary,
  },
  couponSection: {
    marginTop: 8,
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
  },
}))
